import { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { routes, Section } from '@/utils/routes'
import { currentPathname } from '@/utils/currentPathname'
import { leadsApi, type WorkspaceLeadAnalyticsResponseDto, type LeadSignalTimelineDto, type CompanySignalDto } from '@/api/leads'
import { SignalBars } from '@/components/common/SignalBars'
import AudienceViewToggle from '@/components/common/AudienceViewToggle'
import { DEFAULT_AUDIENCE_VIEW, emptyStateMessage, filterCompaniesByAudience, filterLeadsByAudience, type AudienceView } from '@/utils/leadAudienceFilter'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'
import { PersonAvatar } from '@/components/common/Avatar'
import { CompanyNameCell } from '@/components/companies/CompanyNameCell'
import { getWebTypeMeta } from '@/constants/webTypes'
import { timeFromNowVerbose } from '@/utils/time'
import { RefreshCcw } from 'lucide-preact'
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting'
import { getLeadDisplayName, shouldShowEmailLine } from '@/utils/leadFormatting'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import { useCompany } from '@/hooks/useCompany'
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport'

interface Props { clientCompanyId: string; workspaceId: string }
type Mode = 'contacts' | 'companies'

/**
 * The Contacts/Companies choice lives in the URL, the way web scope has always done it (ORB-623).
 *
 * It was local state here, which made the workspace companies list the only one of the three
 * scopes with no address — so it could not be linked to, refreshed back into, or used as the
 * breadcrumb parent for a company profile. That last one is why this moved: a company page had
 * nowhere to point "back" at except People, which is not where a company lives.
 */
function useWorkspaceLeadsMode(clientCompanyId: string, workspaceId: string): [Mode, (m: Mode) => void] {
  const location = useLocation()
  const mode: Mode = /\/leads\/companies$/.test(currentPathname(location)) ? 'companies' : 'contacts'

  const setMode = (next: Mode) => {
    if (next === mode) return
    const scoped = routes.scoped(clientCompanyId, workspaceId)
    const target = next === 'companies' ? scoped.leadsCompanies() : scoped.section(Section.Leads)
    try { location.route(target) } catch {
      if (typeof window !== 'undefined') {
        window.history.pushState({}, '', target)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  }

  return [mode, setMode]
}

// Render a host link (strips leading www.) falling back to full URL if parsing fails
function companyHostAnchor(companyUrl: string) {
  try {
    const host = new URL(companyUrl).hostname.replace(/^www\./, '')
    return <a href={companyUrl} target="_blank" rel="noopener noreferrer" class="hover:underline" title={companyUrl}>{host}</a>
  } catch {
    return <a href={companyUrl} target="_blank" rel="noopener noreferrer" class="hover:underline" title={companyUrl}>{companyUrl}</a>
  }
}

export const WorkspaceLeads: FunctionalComponent<Props> = ({ clientCompanyId, workspaceId }) => {
  const { company, currentWorkspace } = useCompany()
  const [data, setData] = useState<WorkspaceLeadAnalyticsResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useWorkspaceLeadsMode(clientCompanyId, workspaceId)
  const [audienceView, setAudienceView] = useState<AudienceView>(DEFAULT_AUDIENCE_VIEW)
  const [showNonProspects, setShowNonProspects] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LeadSignalTimelineDto | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [companySearch, setCompanySearch] = useState('')
  const EMPTY: WorkspaceLeadAnalyticsResponseDto = { Leads: [], Companies: [], LeadCount: 0, NonProspectCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() }

  const load = (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    leadsApi.getWorkspaceLeads(workspaceId, signal)
      .then(r => { const payload = r?.data; if (payload) setData(payload); else setData(EMPTY) })
      .catch(e => {
        if (e instanceof Error && e.name === 'AbortError') return
        setError(e?.message || 'Unknown error')
        setData(EMPTY)
      })
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    const abortController = new AbortController()
    load(abortController.signal)
    return () => abortController.abort()
  }, [workspaceId])

  const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—'
  const leads = data?.Leads || []
  const companies = data?.Companies || []
  // Companies follow the same non-prospect switch as Contacts, or the two modes
  // disagree about whether ORTEC and OrbitalX are in this campaign (ORB-619).
  const visibleCompanies = filterCompaniesByAudience(companies, audienceView, showNonProspects)
  const filteredLeads = mode === 'contacts' ? filterLeadsByAudience(leads, audienceView, showNonProspects) : leads

  useEffect(() => { if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId)) setSelectedLead(null) }, [filteredLeads, selectedLead])

  // Narrowed before the grid sees the rows, so its empty state, its row count and the CSV export
  // all agree — and the search box outlives a query that matches nothing.
  //
  // A query per mode: only one grid renders at a time, and carrying "acme" from Contacts into
  // Companies would silently narrow a list nobody searched.
  const searchedLeads = useMemo(() => {
    const query = contactSearch.trim().toLowerCase()
    if (!query) return filteredLeads
    return filteredLeads.filter(lead =>
      [lead.FullName, lead.FirstName, lead.LastName, lead.Email, lead.JobTitle, lead.Company]
        .filter(Boolean).join(' ').toLowerCase().includes(query))
  }, [filteredLeads, contactSearch])

  const searchedCompanies = useMemo(() => {
    const query = companySearch.trim().toLowerCase()
    if (!query) return visibleCompanies
    return visibleCompanies.filter(company =>
      [company.CompanyName, company.CompanyWebsite, company.Industry]
        .filter(Boolean).join(' ').toLowerCase().includes(query))
  }, [visibleCompanies, companySearch])

  // ---------- Contact columns for DataGrid ----------
  const contactColumns = useMemo((): ColumnConfig<LeadSignalTimelineDto>[] => [
    {
      key: 'lead',
      header: 'Lead',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (l) => {
        const name = getLeadDisplayName(l)
        const showEmail = shouldShowEmailLine(l, name)
        return (
          <div class="flex items-center gap-3">
            <PersonAvatar firstName={l.FirstName} lastName={l.LastName} fullName={l.FullName} profilePicUrl={l.ProfilePicUrl} alt={name} />
            <div class="flex flex-col leading-tight min-w-0">
              <span class="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]" title={name}>{name}</span>
              {showEmail && <span class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={l.Email as string}>{l.Email}</span>}
            </div>
          </div>
        )
      },
    },
    {
      key: 'company',
      header: 'Company',
      sortable: true,
      className: 'truncate max-w-[160px]',
      render: (l) => <span title={l.Company || '—'}>{l.Company || '—'}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      className: 'truncate max-w-[180px]',
      render: (l) => <span title={l.JobTitle || '—'}>{l.JobTitle || '—'}</span>,
    },
    {
      key: 'types',
      header: 'Types',
      render: (l) => (
        <div class="flex flex-wrap gap-1">
          {l.WebTypes?.map(wt => {
            const meta = getWebTypeMeta(wt as string)
            return <span key={wt} class={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>{wt}</span>
          })}
        </div>
      ),
    },
    {
      key: 'lastConversion',
      header: 'Last Conversion',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (l) => <>{l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—'}</>,
    },
    {
      key: 'conversions',
      header: 'Conversions',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (l) => <>{l.InteractionCount}</>,
    },
    {
      key: 'signal',
      header: 'Workspace Signal',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (l) => <SignalBars score={l.SignalScore} />,
    },
  ], [])

  // ---------- Company columns for DataGrid ----------
  const companyColumns = useMemo((): ColumnConfig<CompanySignalDto>[] => [
    {
      key: 'company',
      header: 'Company',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (c) => (
        <CompanyNameCell company={c} clientCompanyId={clientCompanyId} workspaceId={workspaceId} />
      ),
    },
    {
      key: 'url',
      header: 'URL',
      className: 'truncate max-w-[180px]',
      render: (c) => c.CompanyWebsite ? companyHostAnchor(c.CompanyWebsite) : <>{'—'}</>,
    },
    {
      key: 'industry',
      header: 'Industry',
      sortable: true,
      className: 'truncate max-w-[160px]',
      render: (c) => <span title={c.Industry || '—'}>{c.Industry || '—'}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      sortable: true,
      render: (c) => <>{c.StaffCountRange || '—'}</>,
    },
    {
      key: 'lastConversion',
      header: 'Last Conversion',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (c) => <>{c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—'}</>,
    },
    {
      key: 'leads',
      header: 'Leads',
      sortable: true,
      render: (c) => <>{c.LeadCount}</>,
    },
    {
      key: 'signal',
      header: 'Workspace Signal',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (c) => <SignalBars score={c.SignalScore} />,
    },
  ], [])

  // ---------- Export configs for DataGrid ----------
  const contactExportConfig = useMemo(() => ({
    getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Workspace', 'leads'),
    columns: LEAD_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
  }), [company?.Name, currentWorkspace?.Name])

  const companyExportConfig = useMemo(() => ({
    getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Workspace', 'companies'),
    columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
  }), [company?.Name, currentWorkspace?.Name])

  // Shared page header component
  const PageHeader = () => (
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white tracking-tight truncate">Workspace Leads</h1>
        <p class="text-slate-600 dark:text-slate-300">All leads from within the workspace</p>
      </div>
    </div>
  )

  const ModeSwitch = () => {
    const options: Array<{ key: Mode; label: string }> = [
      { key: 'contacts', label: 'Contacts' },
      { key: 'companies', label: 'Companies' }
    ]
    return (
      <div role="radiogroup" aria-label="Leads view mode" class="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
        {options.map(o => {
          const active = o.key === mode
          return (
            <button
              key={o.key}
              role="radio"
              aria-checked={active}
              onClick={() => !active && setMode(o.key)}
              class={`px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10 ${active ? 'bg-green-600 text-white dark:bg-green-500' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    )
  }

  const MetricsBar = () => (
    <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
        <span><span class="font-semibold text-gray-900 dark:text-gray-100">Conversions:</span> {data?.LeadCount ?? '—'}</span>
        <span><span class="font-semibold text-gray-900 dark:text-gray-100">Companies:</span> {data?.CompanyCount ?? '—'}</span>
        <span class="hidden xs:inline"><span class="font-semibold text-gray-900 dark:text-gray-100">Generated:</span> {generatedRelative}</span>
        <AudienceViewToggle
            view={audienceView}
            onChange={setAudienceView}
            showNonProspects={showNonProspects}
            onToggleNonProspects={() => setShowNonProspects(v => !v)}
            // Counts what the switch would actually reveal in the mode you're in: hidden
            // leads in Contacts, hidden companies in Companies. They differ a lot — 27
            // internal registrants can sit behind only 3 companies.
            nonProspectCount={mode === 'contacts'
              ? data?.NonProspectCount
              : companies.filter(company => company.IsNonProspect).length}
                      />
      </div>
      <div class="flex items-center gap-2">
        <ModeSwitch />
        <button onClick={() => load()} class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" title="Refresh">
          <RefreshCcw class="w-4 h-4" />
          Refresh
        </button>
      </div>
    </div>
  )

  if (loading) return (
    <div class="px-6 pt-4 lg:pt-6 pb-6 space-y-6">
      <PageHeader />
      <div class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <MetricsBar />
        <div class="p-4 text-sm text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    </div>
  )
  if (error) return (
    <div class="px-6 pt-4 lg:pt-6 pb-6 space-y-6">
      <PageHeader />
      <div class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <MetricsBar />
        <div class="p-4 text-sm text-red-600 dark:text-red-400">Failed: {error}</div>
      </div>
    </div>
  )

  const showLeads = mode === 'contacts'
  const showCompanies = mode === 'companies'
  if (showLeads && filteredLeads.length === 0) return (
    <div class="px-6 pt-4 lg:pt-6 pb-6 space-y-6">
      <PageHeader />
      <div class="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 p-6 text-center text-sm text-gray-600 dark:text-gray-400">
        <MetricsBar />
        <div class="mt-6">{emptyStateMessage(audienceView, showNonProspects, leads.length)}</div>
      </div>
    </div>
  )
  if (showCompanies && visibleCompanies.length === 0) return (
    <div class="px-6 pt-4 lg:pt-6 pb-6 space-y-6">
      <PageHeader />
      <div class="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 p-6 text-center text-sm text-gray-600 dark:text-gray-400">
        <MetricsBar />
        <div class="mt-6">No companies yet.</div>
      </div>
    </div>
  )

  return (
    <div class="px-6 pt-4 lg:pt-6 pb-10 space-y-6">
      <PageHeader />
      <div>
        <MetricsBar />
        {showLeads && (
          <DataGrid<LeadSignalTimelineDto>
            title="Contacts"
            data={searchedLeads}
            columns={contactColumns}
            getRowKey={(l) => l.LeadId}
            onRowClick={setSelectedLead}
            getSortComparator={getLeadComparator}
            scrollable
            pagination
            columnVisibilityKey="orbitalx-lead-columns-workspace"
            exportConfig={contactExportConfig}
            search={{ value: contactSearch, onChange: setContactSearch, placeholder: 'Search contacts', ariaLabel: 'Search contacts' }}
            // Every value that narrows the set, not just the query: switching audience used to leave
            // the reader on a page the new set no longer has.
            pageResetKey={`${contactSearch}|${audienceView}|${showNonProspects}`}
            emptyMessage={contactSearch.trim()
              ? `No contacts match "${contactSearch.trim()}".`
              : emptyStateMessage(audienceView, showNonProspects, leads.length)}
          />
        )}
        {showCompanies && (
          <DataGrid<CompanySignalDto>
            title="Companies"
            data={searchedCompanies}
            columns={companyColumns}
            getRowKey={(c) => c.CompanyId || c.CompanyName}
            getSortComparator={getCompanyComparator}
            scrollable
            pagination
            columnVisibilityKey="orbitalx-company-columns-workspace"
            exportConfig={companyExportConfig}
            search={{ value: companySearch, onChange: setCompanySearch, placeholder: 'Search companies', ariaLabel: 'Search companies' }}
            // The audience switch narrows companies too (ORB-619, above), so it belongs in the key.
            pageResetKey={`${companySearch}|${audienceView}|${showNonProspects}`}
            emptyMessage={companySearch.trim()
              ? `No companies match "${companySearch.trim()}".`
              : 'No companies yet.'}
          />
        )}
      </div>
      {selectedLead && (
        <ContactPeekFlyout
          anchorType="lead"
          anchorId={selectedLead.LeadId}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </div>
  )
}
